import React, { useEffect, useMemo, useState } from 'react';
import { Translate, withLocalize } from 'react-localize-redux';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { PageHeader, PageWrapper, PopupTypes } from 'components/partials';
import FieldsService from '../services/fields';
import { updatedFields } from '../actions/fields';
import GeneralPopup from '../components/organisms/GeneralPopup';
import { Button, Spinner } from '../components/widgets';
import Icon, { IconSizes } from '../components/foundations/Icon';
import toaster from '../utils/toaster';
import { useAsync } from '../utils/customHooks';
import CustomMetricForm from './components/CustomMetricForm';
import { CustomMetricsContext } from './context';
import { NEW_METRIC_ID, SUCCESS_TOAST_AUTO_CLOSE, TRANSLATION_PREFIX, getDefaultCustomMetric } from './consts';
import { generateId, processPageData } from './utils';
import { getCustomMetricsPageData, saveCustomMetric, deleteCustomMetric } from './service';
import css from './page.css';

const PAGE_HEADER_TEXT = 'STATIC.CUSTOM_METRICS';

function CustomMetricsPage({ translate }) {
    const dispatch = useDispatch();
    const [pageDataResponse, runFetchPageData] = useAsync({ isLoading: true });
    const [pageData, setPageData] = useState(null);
    const [customMetrics, setCustomMetrics] = useState([]);
    const [editingMetricIds, setEditingMetricIds] = useState([]);
    const [isCreating, setIsCreating] = useState(false);
    const [metricToDelete, setMetricToDelete] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        runFetchPageData(getCustomMetricsPageData());
    }, []);

    useEffect(() => {
        if (pageDataResponse.data) {
            const processed = processPageData(pageDataResponse.data);
            setPageData(processed);
            setCustomMetrics(processed.customMetrics);
        }
    }, [pageDataResponse.data]);

    const contextValue = useMemo(() => pageData || {}, [pageData]);

    const stopEditing = metricId =>
        setEditingMetricIds(currentIds => currentIds.filter(currentId => currentId !== metricId));

    const refreshReportFields = async () => {
        try {
            const { value: allFields } = await new FieldsService().updateAllFields(true);
            dispatch(updatedFields(allFields));
        } catch (error) {
            // Non-fatal: reports fall back to the previously cached fields until reloaded.
        }
    };

    const onSaveMetric = async metric => {
        setSaving(true);
        try {
            await saveCustomMetric(metric);

            const savedMetric = { ...metric, id: generateId(), oldName: metric.name };

            if (metric.id === NEW_METRIC_ID) {
                setCustomMetrics(currentMetrics => [savedMetric, ...currentMetrics]);
                setIsCreating(false);
            } else {
                setCustomMetrics(currentMetrics =>
                    currentMetrics.map(currentMetric => (currentMetric.id === metric.id ? savedMetric : currentMetric))
                );
                stopEditing(metric.id);
            }

            refreshReportFields();
            toaster.success(<Translate id={`${TRANSLATION_PREFIX}.CUSTOM_METRIC_CREATED`} />, {
                autoClose: SUCCESS_TOAST_AUTO_CLOSE,
            });
        } finally {
            setSaving(false);
        }
    };

    const onDeleteApproved = async () => {
        try {
            await deleteCustomMetric(metricToDelete.name);
            setCustomMetrics(currentMetrics =>
                currentMetrics.filter(currentMetric => currentMetric.id !== metricToDelete.id)
            );
            stopEditing(metricToDelete.id);
            refreshReportFields();
        } catch (error) {
            toaster.error(error?.text || translate(`${TRANSLATION_PREFIX}.DELETE_ERROR`));
        } finally {
            setMetricToDelete(null);
        }
    };

    if (pageDataResponse.isLoading) {
        // Bare, not inside PageWrapper: the wrapper is header-sized while loading, so the
        // expanded spinner would stick to the top instead of centering in the page body.
        return <Spinner show expanded />;
    }

    if (pageDataResponse.error || !pageData) {
        return (
            <PageWrapper>
                <PageHeader text={PAGE_HEADER_TEXT} subText={{ subTitle: `${TRANSLATION_PREFIX}.PREAMBLE` }} />
                <div className={css.generalError}>
                    <Translate id={`${TRANSLATION_PREFIX}.GENERAL_ERROR`} />
                </div>
            </PageWrapper>
        );
    }

    return (
        <CustomMetricsContext.Provider value={contextValue}>
            <PageWrapper className={css.wrapper}>
                <PageHeader text={PAGE_HEADER_TEXT} subText={{ subTitle: `${TRANSLATION_PREFIX}.PREAMBLE` }} />
                <div className={css.toolbar}>
                    <Button
                        type="primary"
                        text="STATIC.BUTTONS.NEW_CUSTOM_METRIC"
                        disabled={isCreating}
                        onClick={() => setIsCreating(true)}
                    />
                </div>
                {!customMetrics.length && !isCreating && (
                    <h2 className={css.emptyState}>
                        <Translate id={`${TRANSLATION_PREFIX}.NO_CUSTOM_METRICS_DEFINED_IMPLORE`} />
                    </h2>
                )}
                <div className={css.metricsList}>
                    {isCreating && (
                        <div className={css.metricCard}>
                            <CustomMetricForm
                                metric={getDefaultCustomMetric()}
                                onSave={onSaveMetric}
                                onCancel={() => setIsCreating(false)}
                                saving={saving}
                            />
                        </div>
                    )}
                    {customMetrics.map(metric =>
                        editingMetricIds.includes(metric.id) ? (
                            <div key={metric.id} className={css.metricCard}>
                                <CustomMetricForm
                                    metric={metric}
                                    onSave={onSaveMetric}
                                    onCancel={() => stopEditing(metric.id)}
                                    onDelete={() => setMetricToDelete(metric)}
                                    saving={saving}
                                />
                            </div>
                        ) : (
                            <div key={metric.id} className={css.metricCard}>
                                <div className={css.metricSummary}>
                                    <div className={css.metricSummaryText}>
                                        <span className={css.metricDisplayName}>{metric.display_name}</span>
                                        <span className={css.metricName}>
                                            {metric.name}
                                            {metric.visible ? '' : ' (Invisible)'}
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        className={css.editButton}
                                        title="Edit Custom Metric"
                                        onClick={() =>
                                            setEditingMetricIds(currentIds => [...currentIds, metric.id])
                                        }
                                    >
                                        <Icon name="edit-2-line" size={IconSizes.SMALL} />
                                    </button>
                                </div>
                            </div>
                        )
                    )}
                </div>
                <GeneralPopup
                    open={!!metricToDelete}
                    type={PopupTypes.DELETE}
                    title={`${TRANSLATION_PREFIX}.DELETE_MODAL.TITLE`}
                    text={`${TRANSLATION_PREFIX}.DELETE_MODAL.MESSAGE`}
                    acceptText={`${TRANSLATION_PREFIX}.DELETE_MODAL.ACCEPT_BUTTON`}
                    rejectText={`${TRANSLATION_PREFIX}.DELETE_MODAL.REJECT_BUTTON`}
                    onAccept={onDeleteApproved}
                    onReject={() => setMetricToDelete(null)}
                    showSpinner={false}
                />
            </PageWrapper>
        </CustomMetricsContext.Provider>
    );
}

CustomMetricsPage.propTypes = {
    translate: PropTypes.func.isRequired,
};

export default withLocalize(CustomMetricsPage);
